'use client';

import { useCallback, useEffect, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import { PopupTargetActionEvents } from '~containers/PopupResolver/PopupResolver.types';
import { PopupScheduler } from '~helpers/PopupStorage/PopupScheduler';
import { PopupStorage } from '~helpers/PopupStorage/PopupStorage';
import { stripLocale } from '~helpers/url/localeUtils';
import { usePopupTargetActionEvent } from '~hooks/usePopupTargetActionEvent';
import { Goals } from '~services/Goals';
import { PopupActionStore } from '~store/PopupActionStore';
import {
  ActivePopup,
  PopupApiResponse,
  PopupSegment,
} from '~types/PopupStorage.types';

const fetcher = (url: string): Promise<PopupApiResponse> =>
  fetch(url).then((r) => r.json());

export function usePopupManager(locale: string) {
  const [activePopup, setActivePopup] = useState<ActivePopup | null>(null);

  const [segment] = useState<PopupSegment>(() =>
    typeof window !== 'undefined' ? PopupStorage.determineSegment() : 'new',
  );

  const rawPath =
    typeof window !== 'undefined' ? stripLocale(window.location.pathname) : '/';

  const { data } = useSWRImmutable<PopupApiResponse>(
    typeof window !== 'undefined'
      ? `/blog/api/popups/getPopups/?locale=${locale}&segment=${segment}&path=${encodeURIComponent(rawPath)}`
      : null,
    fetcher,
  );

  const [scheduler] = useState(
    () =>
      new PopupScheduler({
        onShow: (popup) => {
          PopupActionStore.setActivePopupId(popup.id);
          setActivePopup(popup);

          Goals.ym({
            action: 'popup_shown',
            category: 'popup',
            label: popup.id,
          });
          Goals.tmr({ goal: `popup_shown_${popup.id}` });
        },
        onClose: () => {
          PopupActionStore.setActivePopupId(null);
          setActivePopup(null);
        },
      }),
  );

  const closePopup = useCallback(() => {
    scheduler.close();
  }, [scheduler]);

  const handleTargetAction = useCallback(
    (
      popupId: string,
      actionValue: string,
      options?: { shouldClose?: boolean; events?: PopupTargetActionEvents },
    ) => {
      const { shouldClose = false, events } = options ?? {};

      const { handled } = scheduler.recordTargetAction(
        popupId,
        actionValue,
        shouldClose,
      );

      if (!handled) return;

      if (events?.ymEvent) {
        Goals.ym({
          action: events.ymEvent,
          category: 'popup',
          label: `${popupId}:${actionValue}`,
        });
      }

      if (events?.tmrEvent) {
        Goals.tmr({ goal: events.tmrEvent });
      }
    },
    [scheduler],
  );

  useEffect(() => {
    if (!data) return;

    if (data.cooldownTime) {
      scheduler.setCooldownSeconds(data.cooldownTime);
    }

    scheduler.setItems(data.items ?? []);
    scheduler.start();
  }, [data, scheduler]);

  usePopupTargetActionEvent(handleTargetAction);

  useEffect(() => {
    return () => {
      scheduler.dispose();
    };
  }, [scheduler]);

  return {
    activePopup,
    closePopup,
    handleTargetAction,
  };
}
